/**
 * Default player state factory
 * Creates initial game state for new players
 */

import { PlayerState, Quality, EquipmentSlot, TrapStatus, DEFAULT_STATS, calculateXPForLevel } from '@/types';
import { BALANCE_CONFIG } from '../balance/balanceConfig';

/**
 * Create a fresh player state (new game)
 */
export function createDefaultPlayerState(): PlayerState {
  return {
    // Meta
    version: BALANCE_CONFIG.SAVE_VERSION,
    createdAt: Date.now(),
    lastSavedAt: Date.now(),

    // Currency
    currency: BALANCE_CONFIG.STARTING_CURRENCY,

    // RPG Progression
    progression: {
      stats: DEFAULT_STATS,
      progress: {
        level: 1,
        currentXP: 0,
        xpToNextLevel: calculateXPForLevel(2),
        unspentStatPoints: 0,
      },
    },

    // Zone Progression
    currentZone: 'mountain_lake',
    unlockedZones: ['mountain_lake'],

    // Equipment
    ownedEquipment: {
      rod_wooden: { ownedAt: Date.now(), quantity: 1, timesUsed: 0 },
      reel_basic: { ownedAt: Date.now(), quantity: 1, timesUsed: 0 },
      bait_worms: { ownedAt: Date.now(), quantity: 10, timesUsed: 0 },
      line_mono: { ownedAt: Date.now(), quantity: 1, timesUsed: 0 },
      bobber_basic: { ownedAt: Date.now(), quantity: 1, timesUsed: 0 },
    },

    equippedGear: {
      [EquipmentSlot.ROD]: 'rod_wooden',
      [EquipmentSlot.REEL]: 'reel_basic',
      [EquipmentSlot.BAIT]: 'bait_worms',
      [EquipmentSlot.LINE]: 'line_mono',
      [EquipmentSlot.TACKLE]: 'bobber_basic',
    },

    // Inventory
    inventory: {
      stacks: [],
      capacity: BALANCE_CONFIG.STARTING_INVENTORY_CAPACITY,
      currentCount: 0,
    },

    // Fishing Buddies (Phase 1 - simplified)
    fishingBuddies: [],

    // Hauler Buddies (Phase 1 - simplified)
    haulerBuddies: [],

    // Quests
    quests: {
      activeQuests: [],
      completedQuests: [],
      failedQuests: [],
      availableQuests: [],
      lastRefresh: Date.now(),
      acceptedQuestDefinitions: {},
    },

    // Shops
    shops: {
      unlockedOutfitters: ['lakeside_tackle'], // Start with default outfitter
      unlockedMarkets: ['camp_chef'], // Start with default market
      visitedShops: {},
      purchaseHistory: [],
    },

    // Auto-sell protection settings
    autoSellSettings: {
      protectedFish: {}, // Start with no protections
    },

    // Fish discovery tracking
    discoveredFish: [], // Start with no discoveries

    // Trap state
    trapState: {
      status: TrapStatus.EMPTY,
      soakStartedAt: null,
      soakDurationMs: 0,
      readyAt: null,
      cooldownEndsAt: null,
      selectedTrapBait: null,
      totalTrapsSet: 0,
      totalTrapsPulled: 0,
      totalCreaturesCaught: 0,
      totalTrapValueEarned: 0,
    },

    // Milestones
    earnedMilestones: [],

    // Statistics
    stats: {
      totalFishCaught: 0,
      totalFishSold: 0,
      totalCurrencyEarned: 0,
      totalClicks: 0,
      playtimeSeconds: 0,
      fishCaughtBySpecies: {},
      fishCaughtByQuality: {
        [Quality.COMMON]: 0,
        [Quality.UNCOMMON]: 0,
        [Quality.RARE]: 0,
        [Quality.EPIC]: 0,
        [Quality.LEGENDARY]: 0,
        [Quality.UNIQUE]: 0,
        [Quality.QUEST]: 0,
      },
      statsByZone: {},
    },

    // Settings
    settings: {
      soundEnabled: true,
      musicEnabled: true,
      soundVolume: 0.7,
      musicVolume: 0.5,
      showNotifications: true,
      showFishingAnimation: true,
      reducedMotion: false,
      autoSaveInterval: BALANCE_CONFIG.AUTOSAVE_INTERVAL,
      confirmSells: false,
      hasSeenTimingTutorial: false,
      fontSize: 1.0,
    },
  };
}

/**
 * Create default player stats (before any equipment)
 */
export function createDefaultPlayerStats() {
  return {
    fishingSpeed: 0,
    catchChance: 0,
    qualityBonus: 0,
    valueBonus: 0,
    effectiveFishingTime: BALANCE_CONFIG.BASE_FISHING_TIME,
    effectiveCatchRate: BALANCE_CONFIG.BASE_CATCH_CHANCE,
  };
}
